import { useMemo, type ReactElement } from 'react';
import { HairlineCard, Stack, Tab, Tabs, Typography } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type { Permission } from '@munifocus/access';
import { AppShell } from '../../components/AppShell';
import { PageIntro } from '../../components/PageIntro';
import { useScope } from '../../providers/ScopeProvider';
import { ContractsPanel } from './ContractsPanel';
import { AppointmentsPanel } from './AppointmentsPanel';
import { MembersPanel } from './MembersPanel';
import { EmployeesPanel } from './EmployeesPanel';
import { SigningAuthorityPanel } from './SigningAuthorityPanel';
import { MyAccessPanel } from './MyAccessPanel';
import { LgContactsPanel } from './LgContactsPanel';
import { BylawWorkflowsPanel } from './BylawWorkflowsPanel';
import { SeatsPanel } from './SeatsPanel';
import { CompanyBillingPanel } from './CompanyBillingPanel';
import { MunicipalInvitationsPanel } from './MunicipalInvitationsPanel';

/** One tab, and the permission that earns it a place. */
interface AdminTab {
  key: string;
  labelKey: string;
  /** Null means everyone — "My access" shows an account its own standing. */
  permission: Permission | null;
  render: () => ReactElement;
}

/**
 * The Government Portal's administration section.
 *
 * Every tab is permission-gated, and a person only ever sees the tabs they can
 * act in. That is deliberate: a council member and a contractor's operations
 * director both land here, and showing each other's tabs greyed out would tell
 * them what they are missing rather than what they can do.
 *
 * The tab set is derived from the *active scope*, so switching from a
 * municipality to a company changes the section rather than filtering it.
 */
export function Administration() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const { can, active, isLoading } = useScope();

  const tabs = useMemo<AdminTab[]>(() => {
    const all: AdminTab[] = [
      {
        key: 'contracts',
        labelKey: 'gov.admin.tabs.contracts',
        permission: 'contract:read',
        render: () => <ContractsPanel />,
      },
      {
        key: 'officers',
        labelKey: 'gov.admin.tabs.officers',
        permission: 'officer:read',
        render: () => <AppointmentsPanel />,
      },
      {
        key: 'members',
        labelKey: 'gov.admin.tabs.members',
        permission: 'member:read',
        render: () => <MembersPanel />,
      },
      {
        key: 'employees',
        labelKey: 'gov.admin.tabs.employees',
        permission: 'employee:read',
        render: () => <EmployeesPanel />,
      },
      {
        // Only in a company scope (`employee:*` applies nowhere else): the
        // company's named enforcement seats — "N of M", who holds them.
        key: 'seats',
        labelKey: 'gov.admin.tabs.seats',
        permission: 'employee:read',
        render: () => <SeatsPanel />,
      },
      {
        // Inviting a municipality's clerk to approve the company's officers —
        // including at a municipality that does not subscribe.
        key: 'municipalAdministrators',
        labelKey: 'gov.admin.tabs.municipalAdministrators',
        permission: 'employee:manage',
        render: () => <MunicipalInvitationsPanel />,
      },
      {
        // The company's Munifocus licence and invoices.
        key: 'billing',
        labelKey: 'gov.admin.tabs.billing',
        permission: 'employee:manage',
        render: () => <CompanyBillingPanel />,
      },
      {
        // Only in a company scope: the municipalities this contractor brought
        // in on the limited licence, and the people it has invited there.
        key: 'lgContacts',
        labelKey: 'gov.admin.tabs.lgContacts',
        permission: 'employee:manage',
        render: () => <LgContactsPanel />,
      },
      {
        key: 'signing',
        labelKey: 'gov.admin.tabs.signing',
        permission: 'signing:read',
        render: () => <SigningAuthorityPanel />,
      },
      {
        // The by-law types a municipality runs and the citations it prints.
        // Read by anyone who reads its laws; the citations are edited only
        // with `workflow:configure`, which the panel checks itself.
        key: 'bylawWorkflows',
        labelKey: 'gov.admin.tabs.bylawWorkflows',
        permission: 'law:read',
        render: () => <BylawWorkflowsPanel />,
      },
      {
        key: 'me',
        labelKey: 'gov.admin.tabs.myAccess',
        permission: null,
        render: () => <MyAccessPanel />,
      },
    ];
    return all.filter((entry) => entry.permission === null || can(entry.permission));
  }, [can]);

  const activeKey = tabs.find((entry) => entry.key === tab)?.key ?? tabs[0]?.key;
  const current = tabs.find((entry) => entry.key === activeKey);

  return (
    <AppShell>
      <PageIntro>
        {active ? t('gov.admin.subtitleScoped', { scope: active.name }) : t('gov.admin.subtitle')}
      </PageIntro>
      <Stack spacing={2.5}>
        {!isLoading && tabs.length === 0 ? (
          <HairlineCard sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('gov.admin.empty')}
            </Typography>
          </HairlineCard>
        ) : (
          <>
            <Tabs
              value={activeKey ?? false}
              onChange={(_, value: string) => navigate(`/administration/${value}`)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label={t('gov.admin.title')}
            >
              {tabs.map((entry) => (
                <Tab key={entry.key} value={entry.key} label={t(entry.labelKey)} />
              ))}
            </Tabs>
            {current?.render()}
          </>
        )}
      </Stack>
    </AppShell>
  );
}
